import { Popover } from '@material-ui/core'
import React, { useRef, useState } from 'react'

/**
 * Anchor-and-popover helper.
 *
 * `expanded` is either a node (the original contract, unchanged for all existing call
 * sites) or a render function `({ open }) => node`.
 *
 * `lazy` opts out of `keepMounted`. Default false so nothing existing changes behaviour:
 * these popovers hold static content, and keeping them mounted is what makes them open
 * instantly. Pass it only when the body does work — a fetch, an expensive render —
 * because otherwise that work happens once per row as soon as the table paints.
 */
function ExpandOnClick({ children, expanded, lazy = false, ...rest }) {
  const anchorElRef = useRef()
  const [popoverIsOpen, setPopoverIsOpen] = useState(false)

  const handlePopoverOpen = () => {
    setPopoverIsOpen(true)
  }

  const handlePopoverClose = () => {
    setPopoverIsOpen(false)
  }

  return (
    <>
      {children({ ref: anchorElRef, onClick: handlePopoverOpen })}
      <Popover
        open={popoverIsOpen}
        anchorEl={anchorElRef.current}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        keepMounted={!lazy}
        {...rest}
      >
        {/* `expanded` may be a node (every existing call site) or a render function.
            The function form exists because `keepMounted` mounts the popover body for
            every row on page load, not when it opens: a body containing a data fetch
            would therefore fire one request per row the moment the table renders — 100
            requests for a page nobody has clicked on. Passing `lazy` turns keepMounted
            off and defers the call until the popover is actually open, so the fetch
            happens once, on demand. */}
        {typeof expanded === 'function'
          ? popoverIsOpen || !lazy
            ? expanded({ open: popoverIsOpen })
            : null
          : expanded}
      </Popover>
    </>
  )
}

export default ExpandOnClick
